// decision-answered-journal-event.ts — V2-0 (spec V2 §4.0, plan 20261006-1008
// phase-01).
//
// Event journal `decision_answered`: người đã chốt một câu hỏi. Đây là nguồn
// nhánh NGƯỜI của sổ quyết định (§4.1, "journal-backed") — trước V2-0 câu trả
// lời chỉ nằm trong interview.json, bị ghi đè khi người đổi ý, không có dấu
// vết ai chọn gì lúc nào.
//
// Idempotent theo `directive_id`: crash giữa lúc append journal và
// `markDirectiveProcessed` ⇒ lần xử lý lại thấy event đã có và KHÔNG ghi lần
// hai. Kiểm bằng quét journal (không thêm field vào projection mission.json —
// reducer bỏ qua kind này qua `default`, như mọi event audit khác).
import { join } from "node:path";
import { optionLabelForId, resolveAnswerValues } from "./interview-answer-option-id-resolution.js";
import { scanJournal } from "./journal-scan-report.js";
import type { MissionDelta } from "./mission-state-types.js";
import { MISSION_JOURNAL_FILE_NAME } from "./mission-store.js";
import { needsDecisionQuestionTicket } from "./needs-decision-question-ticket-match.js";
import type { InterviewQuestion } from "./schemas/interview-schema.js";

/** `directive_id` của mọi `decision_answered` đã ghi bền trong journal mission. */
export async function readJournaledDecisionDirectiveIds(controlRoot: string): Promise<Set<string>> {
  const ids = new Set<string>();
  for (const [, directiveId] of await journaledDecisionAnswers(controlRoot)) ids.add(directiveId);
  return ids;
}

/** Khoá replay (question_id, directive_id) — cùng khoá dedupe của decision-ledger-projection.ts. */
export function decisionAnswerKey(questionId: string, directiveId: string): string {
  return `${questionId}\u0000${directiveId}`;
}

/**
 * 06i R1 (N6): khoá `decisionAnswerKey` của mọi `decision_answered` đã ghi bền —
 * MỘT redirect có thể answer NHIỀU câu treo của cùng ticket, nên replay theo
 * `directive_id` đơn thuần sẽ bỏ sót câu thứ hai.
 */
export async function readJournaledDecisionAnswerKeys(controlRoot: string): Promise<Set<string>> {
  const keys = new Set<string>();
  for (const [questionId, directiveId] of await journaledDecisionAnswers(controlRoot)) {
    keys.add(decisionAnswerKey(questionId, directiveId));
  }
  return keys;
}

async function journaledDecisionAnswers(controlRoot: string): Promise<Array<[string, string]>> {
  const scan = await scanJournal(join(controlRoot, MISSION_JOURNAL_FILE_NAME));
  const pairs: Array<[string, string]> = [];
  for (const record of scan.records) {
    const { kind, question_id: questionId, directive_id: directiveId } = record.delta;
    if (kind === "decision_answered" && typeof directiveId === "string") {
      pairs.push([typeof questionId === "string" ? questionId : "", directiveId]);
    }
  }
  return pairs;
}

/**
 * `{question_id, ticket_id?, value, actor, directive_id}` — `actor` nguyên chuỗi:
 * `user:<id>` (directive answer) hoặc `redirect:<directive_id>` (06i R1: amend
 * ghi option id vào patch thay cho answer).
 * `ticket_id` (đọc từ nguyên id) vắng ở câu của cả mission.
 *
 * V2-0 ghi `value` (nhãn). V2-1 (E3): câu có `option_ids` và người chọn một
 * option ⇒ thêm `option_id` (đã chuẩn hoá: id, hoặc nhãn duy nhất ⇒ id) và
 * `value` VẪN là nhãn (spec bảng §5.1) — journal tự dựng được sổ khi mất
 * interview.json. Câu v1 giữ nguyên payload V2-0.
 *
 * V2-1 (§4.1 (15)): `decided_at` = `answered_at` của câu trả lời — cột "thời
 * điểm" của sổ khi mất interview.json. Event V2-0 (3.34.3) không có ⇒ sổ để trống.
 */
export function decisionAnsweredDelta(
  question: InterviewQuestion,
  value: string | string[],
  actor: string,
  directiveId: string,
  decidedAt?: string,
): MissionDelta {
  const ticketId = needsDecisionQuestionTicket(question);
  const resolved =
    typeof value === "string" && question.option_ids !== undefined
      ? resolveAnswerValues(question, [value])
      : undefined;
  const optionId = resolved?.ok === true ? resolved.values[0] : undefined;
  const label = optionId === undefined ? undefined : optionLabelForId(question, optionId);
  return {
    kind: "decision_answered",
    question_id: question.id,
    ...(ticketId === undefined ? {} : { ticket_id: ticketId }),
    ...(optionId === undefined ? {} : { option_id: optionId }),
    value: label ?? value,
    actor,
    directive_id: directiveId,
    ...(decidedAt === undefined ? {} : { decided_at: decidedAt }),
  };
}
